import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Box,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Checkbox,
    FormControlLabel,
    Alert,
    CircularProgress,
    Snackbar,
    Tooltip,
    MenuItem,
    useTheme,
    useMediaQuery,
    Stack,
    Card,
    CardContent,
    CardActions,
    Divider,
    AppBar,
    Toolbar
} from '@mui/material';
import {
    Add as AddIcon,
    CheckCircle as CompleteIcon,
    List as TemplateIcon,
    History as HistoryIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ContentCopy as CloneIcon,
    PlayArrow as StartIcon,
    Description as DraftIcon,
    Save as SaveIcon,
    PictureAsPdf as PdfIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { useProfile } from '../../context/userContext';
import { useAuth0 } from '@auth0/auth0-react';
import {
    getChecklistTemplates,
    createChecklistTemplate,
    updateChecklistTemplate,
    deleteChecklistTemplate,
    cloneChecklistTemplate,
    getChecklistInstances,
    completeChecklist,
    createDraftChecklist,
    getDraftChecklists,
    updateDraftChecklist,
    submitChecklist,
    deleteDraftChecklist,
    exportChecklistPDF
} from '../../network/apis/compliance.api';
import { useToast } from '../../hooks/useToast';

const CHECKLIST_CATEGORIES = ['Pre-Flight', 'Post-Flight', 'Maintenance', 'Emergency', 'Other'];

const Checklists = () => {
    const { currentOrganization, userRole } = useOrganization();
    const { profile } = useProfile();
    const { getAccessTokenSilently } = useAuth0();
    const { toast, showSuccess, showError, hideToast } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [activeTab, setActiveTab] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [instances, setInstances] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
    const [draftDialogOpen, setDraftDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [templateForm, setTemplateForm] = useState({
        name: '',
        description: '',
        category: 'Pre-Flight',
        items: [{ id: 1, text: '', required: true }]
    });
    const [completeForm, setCompleteForm] = useState({
        templateId: null,
        operationId: null,
        droneId: null,
        items: [],
        notes: ''
    });

    // Permissions
    const canManageTemplates = ['pilot', 'manager', 'admin', 'owner'].includes(userRole?.toLowerCase());
    const canComplete = ['pilot', 'manager', 'admin', 'owner'].includes(userRole?.toLowerCase());
    const isViewer = userRole?.toLowerCase() === 'viewer';
    const isPilot = userRole?.toLowerCase() === 'pilot';
    const canViewAllChecklists = ['viewer', 'manager', 'admin', 'owner'].includes(userRole?.toLowerCase());

    useEffect(() => {
        if (currentOrganization?.organizationId) {
            // Force Viewers to only see completed checklists tab
            if (isViewer && activeTab !== 2) {
                setActiveTab(2);
            }
            fetchData();
        }
    }, [currentOrganization, activeTab, isViewer]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            if (activeTab === 0) {
                const data = await getChecklistTemplates(token);
                setTemplates(data.templates || []);
            } else if (activeTab === 1) {
                // Drafts Tab - Pilots only see their own drafts
                const data = await getDraftChecklists(token);
                let filteredDrafts = data.drafts || [];

                // Pilots can only view their own drafts
                if (isPilot && profile?.userId) {
                    filteredDrafts = filteredDrafts.filter(draft =>
                        draft.creator?.userId === profile.userId
                    );
                }

                setDrafts(filteredDrafts);
            } else {
                // Completed Checklists Tab
                const data = await getChecklistInstances(token);
                let filteredInstances = data.instances || [];

                // Pilots can only view their own completed checklists
                // Note: Backend already filters for pilots, but we keep this for consistency
                if (isPilot && profile?.userId) {
                    filteredInstances = filteredInstances.filter(instance =>
                        instance.completedByUser?.userId === profile.userId
                    );
                }
                // Viewers and Managers/Admins/Owners can view all completed checklists

                setInstances(filteredInstances);
            }
        } catch (error) {
            showError('Failed to load checklists');
        } finally {
            setLoading(false);
        }
    };

    // Template Management
    const handleOpenTemplateDialog = (template = null) => {
        if (template) {
            setEditMode(true);
            setSelectedTemplate(template);
            setTemplateForm({
                name: template.name,
                description: template.description || '',
                category: template.category,
                items: template.items || []
            });
        } else {
            setEditMode(false);
            setSelectedTemplate(null);
            setTemplateForm({
                name: '',
                description: '',
                category: 'Pre-Flight',
                items: [{ id: 1, text: '', required: true }]
            });
        }
        setTemplateDialogOpen(true);
    };

    const handleAddItem = () => {
        setTemplateForm({
            ...templateForm,
            items: [...templateForm.items, { id: Date.now(), text: '', required: true }]
        });
    };

    const handleRemoveItem = (id) => {
        setTemplateForm({
            ...templateForm,
            items: templateForm.items.filter(item => item.id !== id)
        });
    };

    const handleItemChange = (id, field, value) => {
        setTemplateForm({
            ...templateForm,
            items: templateForm.items.map(item =>
                item.id === id ? { ...item, [field]: value } : item
            )
        });
    };

    const handleSaveTemplate = async () => {
        try {
            const token = await getAccessTokenSilently();
            if (editMode) {
                await updateChecklistTemplate(token, selectedTemplate.id, templateForm);
                showSuccess('Template updated successfully');
            } else {
                await createChecklistTemplate(token, templateForm);
                showSuccess('Template created successfully');
            }
            setTemplateDialogOpen(false);
            fetchData();
        } catch (error) {
            showError('Failed to save template');
        }
    };

    const handleDeleteTemplate = async () => {
        try {
            const token = await getAccessTokenSilently();
            await deleteChecklistTemplate(token, selectedTemplate.id);
            showSuccess('Template deleted successfully');
            setDeleteDialogOpen(false);
            setSelectedTemplate(null);
            fetchData();
        } catch (error) {
            // Check if it's a 409 conflict (template has instances)
            if (error.response?.status === 409) {
                const message = error.response?.data?.message || 'Cannot delete template with existing checklists';
                showError(message);
            } else {
                showError('Failed to delete template');
            }
        }
    };

    // Clone Template
    const [cloneForm, setCloneForm] = useState({ name: '', description: '' });

    const handleOpenCloneDialog = (template) => {
        setSelectedTemplate(template);
        setCloneForm({
            name: `${template.name} (Copy)`,
            description: template.description || ''
        });
        setCloneDialogOpen(true);
    };

    const handleCloneTemplate = async () => {
        try {
            const token = await getAccessTokenSilently();
            await cloneChecklistTemplate(token, selectedTemplate.id, cloneForm);
            showSuccess('Template cloned successfully');
            setCloneDialogOpen(false);
            setActiveTab(0);
            fetchData();
        } catch (error) {
            showError('Failed to clone template');
        }
    };

    // Draft Checklist Management
    const [draftSetupDialogOpen, setDraftSetupDialogOpen] = useState(false);
    const [draftSetupForm, setDraftSetupForm] = useState({
        templateId: null,
        operationId: '',
        droneId: ''
    });

    const handleOpenDraftSetup = (template) => {
        setDraftSetupForm({
            templateId: template.id,
            operationId: '',
            droneId: ''
        });
        setSelectedTemplate(template);
        setDraftSetupDialogOpen(true);
    };

    const handleStartDraft = async () => {
        try {
            const token = await getAccessTokenSilently();
            const draftData = {
                templateId: draftSetupForm.templateId,
                operationId: draftSetupForm.operationId || null,
                droneId: draftSetupForm.droneId || null,
                items: selectedTemplate.items.map(item => ({ ...item, checked: false, notes: '' }))
            };
            await createDraftChecklist(token, draftData);
            showSuccess('Draft checklist created');
            setDraftSetupDialogOpen(false);
            setActiveTab(1); // Switch to Drafts tab
            fetchData();
        } catch (error) {
            showError('Failed to create draft checklist');
        }
    };

    const handleOpenDraftDialog = (draft) => {
        setSelectedDraft(draft);
        setCompleteForm({
            templateId: draft.templateId,
            items: draft.items || [],
            notes: draft.notes || ''
        });
        setDraftDialogOpen(true);
    };

    const handleUpdateDraft = async () => {
        try {
            const token = await getAccessTokenSilently();
            await updateDraftChecklist(token, selectedDraft.id, {
                items: completeForm.items,
                notes: completeForm.notes
            });
            showSuccess('Draft saved');
            setDraftDialogOpen(false);
            fetchData();
        } catch (error) {
            showError('Failed to save draft');
        }
    };

    const handleSubmitDraft = async () => {
        try {
            const token = await getAccessTokenSilently();
            await submitChecklist(token, selectedDraft.id, {
                items: completeForm.items
            });
            showSuccess('Checklist completed');
            setDraftDialogOpen(false);
            setActiveTab(2); // Switch to Completed tab
            fetchData();
        } catch (error) {
            showError(error.response?.data?.error || 'Failed to submit checklist');
        }
    };

    const handleDeleteDraft = async (draft) => {
        try {
            const token = await getAccessTokenSilently();
            await deleteDraftChecklist(token, draft.id);
            showSuccess('Draft deleted');
            fetchData();
        } catch (error) {
            showError('Failed to delete draft');
        }
    };

    // Complete Checklist
    const handleOpenCompleteDialog = (template) => {
        setSelectedTemplate(template);
        setCompleteForm({
            templateId: template.id,
            operationId: null,
            droneId: null,
            items: template.items.map(item => ({
                ...item,
                checked: false
            })),
            notes: ''
        });
        setCompleteDialogOpen(true);
    };

    const handleToggleItem = (id) => {
        setCompleteForm({
            ...completeForm,
            items: completeForm.items.map(item =>
                item.id === id ? { ...item, checked: !item.checked } : item
            )
        });
    };

    const handleCompleteChecklist = async () => {
        try {
            const token = await getAccessTokenSilently();
            await completeChecklist(token, completeForm);
            showSuccess('Checklist completed successfully');
            setCompleteDialogOpen(false);
            fetchData();
        } catch (error) {
            showError('Failed to complete checklist');
        }
    };

    const handleExportPDF = async (instanceId) => {
        try {
            const token = await getAccessTokenSilently();
            const { blob, filename } = await exportChecklistPDF(token, instanceId);

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showSuccess('Checklist exported successfully');
        } catch (error) {
            showError(error.response?.data?.error || 'Failed to export checklist to PDF');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography
                        variant="h4"
                        gutterBottom
                        sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                    >
                        Checklists
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                        Manage checklist templates and completion history
                    </Typography>
                </Box>
            </Box>

            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, val) => setActiveTab(val)}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    scrollButtons={isMobile ? 'auto' : false}
                >
                    <Tab icon={<TemplateIcon />} label="Templates" iconPosition={isMobile ? "top" : "start"} disabled={isViewer} />
                    <Tab icon={<DraftIcon />} label="My Drafts" iconPosition={isMobile ? "top" : "start"} disabled={isViewer} />
                    <Tab icon={<HistoryIcon />} label="Completed" iconPosition={isMobile ? "top" : "start"} />
                </Tabs>
            </Paper>

            {/* Role-based access information banners */}
            {isViewer && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Viewer Access:</strong> You can only view completed checklists. To create templates or complete checklists, contact your Manager or Admin.
                    </Typography>
                </Alert>
            )}

            {isPilot && activeTab === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Pilot Access:</strong> You can create and edit templates, and complete checklists. You can only view your own completed checklists.
                    </Typography>
                </Alert>
            )}

            {isPilot && activeTab === 1 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Note:</strong> You can only view draft checklists that you have created.
                    </Typography>
                </Alert>
            )}

            {isPilot && activeTab === 2 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <strong>Note:</strong> You can only view checklists that you have completed. To view all organization checklists, contact your Manager or Admin.
                    </Typography>
                </Alert>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Templates Tab */}
                    {activeTab === 0 && (
                        <>
                            {isViewer ? (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Access Restricted:</strong> Viewers cannot access checklist templates. Please navigate to the Completed tab to view completed checklists.
                                    </Typography>
                                </Alert>
                            ) : (
                                <>
                                    <Box display="flex" justifyContent="flex-end" mb={2}>
                                        {canManageTemplates && (
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={() => handleOpenTemplateDialog()}
                                                fullWidth={isMobile}
                                            >
                                                Create Template
                                            </Button>
                                        )}
                                    </Box>

                                    {templates.length === 0 ? (
                                        <Alert severity="info">No checklist templates available.</Alert>
                                    ) : isMobile ? (
                                        // Mobile Card View
                                        <Stack spacing={1.5}>
                                            {templates.map((template) => (
                                                <Card key={template.id} variant="outlined">
                                                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                        {/* Header */}
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                                                    {template.name}
                                                                </Typography>
                                                                {template.description && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                        {template.description}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                <Chip label={template.category} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                            </Box>
                                                        </Box>

                                                        {/* Details */}
                                                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                            <Chip
                                                                label={template.isSystemTemplate ? 'System' : 'Custom'}
                                                                size="small"
                                                                color={template.isSystemTemplate ? 'default' : 'primary'}
                                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                                            />
                                                            <Chip
                                                                label={`${template.items?.length || 0} items`}
                                                                size="small"
                                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                                            />
                                                        </Box>

                                                        {/* Action Buttons */}
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1.5 }}>
                                                            {canComplete && (
                                                                <>
                                                                    <Button
                                                                        fullWidth
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="primary"
                                                                        startIcon={<StartIcon />}
                                                                        onClick={() => handleOpenDraftSetup(template)}
                                                                        sx={{ fontSize: '0.75rem' }}
                                                                    >
                                                                        Start Draft
                                                                    </Button>
                                                                    <Button
                                                                        fullWidth
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={<CompleteIcon />}
                                                                        onClick={() => handleOpenCompleteDialog(template)}
                                                                        sx={{ fontSize: '0.75rem' }}
                                                                    >
                                                                        Quick Complete
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {canManageTemplates && (
                                                                <Box sx={{ display: 'flex', gap: 0.75 }}>
                                                                    <Button
                                                                        fullWidth
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={<CloneIcon />}
                                                                        onClick={() => handleOpenCloneDialog(template)}
                                                                        sx={{ fontSize: '0.75rem' }}
                                                                    >
                                                                        Clone
                                                                    </Button>
                                                                    {!template.isSystemTemplate && (
                                                                        <>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleOpenTemplateDialog(template)}
                                                                                sx={{ padding: '4px' }}
                                                                            >
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => {
                                                                                    setSelectedTemplate(template);
                                                                                    setDeleteDialogOpen(true);
                                                                                }}
                                                                                sx={{ padding: '4px' }}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Stack>
                                    ) : (
                                        // Desktop Table View
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Template Name</TableCell>
                                                        <TableCell>Category</TableCell>
                                                        <TableCell>Items</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell align="right">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {templates.map((template) => (
                                                        <TableRow key={template.id}>
                                                            <TableCell>
                                                                <Typography variant="body1">{template.name}</Typography>
                                                                {template.description && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {template.description}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip label={template.category} size="small" />
                                                            </TableCell>
                                                            <TableCell>{template.items?.length || 0}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={template.isSystemTemplate ? 'System' : 'Custom'}
                                                                    size="small"
                                                                    color={template.isSystemTemplate ? 'default' : 'primary'}
                                                                />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Box display="flex" gap={1} justifyContent="flex-end">
                                                                    {canComplete && (
                                                                        <>
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                color="primary"
                                                                                startIcon={<StartIcon />}
                                                                                onClick={() => handleOpenDraftSetup(template)}
                                                                            >
                                                                                Start Draft
                                                                            </Button>
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                startIcon={<CompleteIcon />}
                                                                                onClick={() => handleOpenCompleteDialog(template)}
                                                                            >
                                                                                Quick Complete
                                                                            </Button>
                                                                        </>
                                                                    )}
                                                                    {canManageTemplates && (
                                                                        <>
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                startIcon={<CloneIcon />}
                                                                                onClick={() => handleOpenCloneDialog(template)}
                                                                            >
                                                                                Clone
                                                                            </Button>
                                                                            {!template.isSystemTemplate && (
                                                                                <>
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={() => handleOpenTemplateDialog(template)}
                                                                                    >
                                                                                        <EditIcon />
                                                                                    </IconButton>
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        color="error"
                                                                                        onClick={() => {
                                                                                            setSelectedTemplate(template);
                                                                                            setDeleteDialogOpen(true);
                                                                                        }}
                                                                                    >
                                                                                        <DeleteIcon />
                                                                                    </IconButton>
                                                                                </>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* Drafts Tab */}
                    {activeTab === 1 && (
                        <>
                            {isViewer ? (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Access Restricted:</strong> Viewers cannot access draft checklists. Please navigate to the Completed tab to view completed checklists.
                                    </Typography>
                                </Alert>
                            ) : (
                                <>
                                    {drafts.length === 0 ? (
                                        <Alert severity="info">No draft checklists. Start a new draft from the Templates tab.</Alert>
                                    ) : isMobile ? (
                                        // Mobile Card View
                                        <Stack spacing={1.5}>
                                            {drafts.map((draft) => {
                                                const completed = draft.items?.filter(i => i.checked).length || 0;
                                                const total = draft.items?.length || 0;
                                                const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

                                                return (
                                                    <Card key={draft.id} variant="outlined">
                                                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                            {/* Header */}
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                                                        {draft.template?.name || 'Unknown'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                        {draft.template?.category}
                                                                    </Typography>
                                                                </Box>
                                                                <Chip
                                                                    label={`${percentage}%`}
                                                                    color={percentage === 100 ? 'success' : percentage > 50 ? 'primary' : 'default'}
                                                                    size="small"
                                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                                />
                                                            </Box>

                                                            {/* Details Grid */}
                                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                                                <Box>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                                        Started
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                                                        {formatDate(draft.createdAt)}
                                                                    </Typography>
                                                                </Box>
                                                                <Box>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                                        Progress
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                                                        {completed}/{total} items
                                                                    </Typography>
                                                                </Box>
                                                                {draft.operation?.name && (
                                                                    <Box sx={{ gridColumn: '1 / -1' }}>
                                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                                            Operation
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                                                            {draft.operation.name}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>

                                                            {/* Action Buttons */}
                                                            <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5 }}>
                                                                <Button
                                                                    fullWidth
                                                                    size="small"
                                                                    variant="contained"
                                                                    startIcon={<EditIcon />}
                                                                    onClick={() => handleOpenDraftDialog(draft)}
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                >
                                                                    Continue
                                                                </Button>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleDeleteDraft(draft)}
                                                                    sx={{ padding: '4px' }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                );
                                            })}
                                        </Stack>
                                    ) : (
                                        // Desktop Table View
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Template</TableCell>
                                                        <TableCell>Started</TableCell>
                                                        <TableCell>Progress</TableCell>
                                                        <TableCell>Operation</TableCell>
                                                        <TableCell align="right">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {drafts.map((draft) => {
                                                        const completed = draft.items?.filter(i => i.checked).length || 0;
                                                        const total = draft.items?.length || 0;
                                                        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

                                                        return (
                                                            <TableRow key={draft.id}>
                                                                <TableCell>
                                                                    <Typography variant="body1">{draft.template?.name || 'Unknown'}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {draft.template?.category}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>{formatDate(draft.createdAt)}</TableCell>
                                                                <TableCell>
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                        <Typography variant="body2">{percentage}%</Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            ({completed}/{total})
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {draft.operation?.name || '-'}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Box display="flex" gap={1} justifyContent="flex-end">
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            startIcon={<EditIcon />}
                                                                            onClick={() => handleOpenDraftDialog(draft)}
                                                                        >
                                                                            Continue
                                                                        </Button>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleDeleteDraft(draft)}
                                                                        >
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* Completed Checklists Tab */}
                    {activeTab === 2 && (
                        <>
                            {instances.length === 0 ? (
                                <Alert severity="info">No completed checklists yet.</Alert>
                            ) : isMobile ? (
                                // Mobile Card View
                                <Stack spacing={1.5}>
                                    {instances.map((instance) => (
                                        <Card key={instance.id} variant="outlined">
                                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                {/* Header */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                                            {instance.template?.name || 'Unknown'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                            {instance.completedByUser?.firstName} {instance.completedByUser?.lastName}
                                                        </Typography>
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleExportPDF(instance.id)}
                                                        sx={{ padding: '4px' }}
                                                    >
                                                        <PdfIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>

                                                {/* Details Grid */}
                                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                            Completed At
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                                            {formatDate(instance.completedAt)}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                            Items Checked
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                                            {instance.items?.filter(i => i.checked).length || 0} / {instance.items?.length || 0}
                                                        </Typography>
                                                    </Box>
                                                    {instance.notes && (
                                                        <Box sx={{ gridColumn: '1 / -1' }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                                Notes
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                                                {instance.notes}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            ) : (
                                // Desktop Table View
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Checklist</TableCell>
                                                <TableCell>Completed By</TableCell>
                                                <TableCell>Completed At</TableCell>
                                                <TableCell>Items</TableCell>
                                                <TableCell>Notes</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {instances.map((instance) => (
                                                <TableRow key={instance.id}>
                                                    <TableCell>{instance.template?.name || 'Unknown'}</TableCell>
                                                    <TableCell>
                                                        {instance.completedByUser?.firstName}{' '}
                                                        {instance.completedByUser?.lastName}
                                                    </TableCell>
                                                    <TableCell>{formatDate(instance.completedAt)}</TableCell>
                                                    <TableCell>
                                                        {instance.items?.filter(i => i.checked).length || 0} /{' '}
                                                        {instance.items?.length || 0}
                                                    </TableCell>
                                                    <TableCell>
                                                        {instance.notes ? (
                                                            <Tooltip title={instance.notes}>
                                                                <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>
                                                                    {instance.notes}
                                                                </Typography>
                                                            </Tooltip>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Export to PDF">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleExportPDF(instance.id)}
                                                            >
                                                                <PdfIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Create/Edit Template Dialog */}
            <Dialog
                open={templateDialogOpen}
                onClose={() => setTemplateDialogOpen(false)}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                {isMobile && (
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={() => setTemplateDialogOpen(false)}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                {editMode ? 'Edit Template' : 'Create Template'}
                            </Typography>
                            <Button autoFocus color="inherit" onClick={handleSaveTemplate}>
                                {editMode ? 'Update' : 'Create'}
                            </Button>
                        </Toolbar>
                    </AppBar>
                )}
                {!isMobile && <DialogTitle>{editMode ? 'Edit Template' : 'Create Template'}</DialogTitle>}
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Template Name"
                            value={templateForm.name}
                            onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={templateForm.description}
                            onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={2}
                        />
                        <TextField
                            fullWidth
                            select
                            label="Category"
                            value={templateForm.category}
                            onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                            margin="normal"
                            required
                        >
                            {CHECKLIST_CATEGORIES.map((cat) => (
                                <MenuItem key={cat} value={cat}>
                                    {cat}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                            Checklist Items
                        </Typography>
                        {templateForm.items.map((item, index) => (
                            <Box
                                key={item.id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    gap: 1,
                                    alignItems: isMobile ? 'stretch' : 'center',
                                    mb: isMobile ? 2 : 1,
                                    p: isMobile ? 1.5 : 0,
                                    border: isMobile ? '1px solid #e0e0e0' : 'none',
                                    borderRadius: isMobile ? 1 : 0
                                }}
                            >
                                <TextField
                                    fullWidth
                                    label={`Item ${index + 1}`}
                                    value={item.text}
                                    onChange={(e) => handleItemChange(item.id, 'text', e.target.value)}
                                    size="small"
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={item.required}
                                                onChange={(e) => handleItemChange(item.id, 'required', e.target.checked)}
                                            />
                                        }
                                        label="Required"
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveItem(item.id)}
                                        disabled={templateForm.items.length === 1}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                        <Button
                            startIcon={<AddIcon />}
                            onClick={handleAddItem}
                            sx={{ mt: 1 }}
                            fullWidth={isMobile}
                        >
                            Add Item
                        </Button>
                    </Box>
                </DialogContent>
                {!isMobile && (
                    <DialogActions>
                        <Button onClick={() => setTemplateDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveTemplate} variant="contained">
                            {editMode ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            {/* Draft Setup Dialog */}
            <Dialog
                open={draftSetupDialogOpen}
                onClose={() => setDraftSetupDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>Start Draft Checklist</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Link this checklist to an operation or drone (optional)
                        </Alert>
                        <TextField
                            fullWidth
                            label="Operation ID (Optional)"
                            type="number"
                            value={draftSetupForm.operationId}
                            onChange={(e) => setDraftSetupForm({ ...draftSetupForm, operationId: e.target.value })}
                            margin="normal"
                            helperText="Enter the operation ID to link this checklist"
                        />
                        <TextField
                            fullWidth
                            label="Drone ID (Optional)"
                            type="number"
                            value={draftSetupForm.droneId}
                            onChange={(e) => setDraftSetupForm({ ...draftSetupForm, droneId: e.target.value })}
                            margin="normal"
                            helperText="Enter the drone ID to link this checklist"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0, p: isMobile ? 2 : 1 }}>
                    <Button onClick={() => setDraftSetupDialogOpen(false)} fullWidth={isMobile}>
                        Cancel
                    </Button>
                    <Button onClick={handleStartDraft} variant="contained" startIcon={<StartIcon />} fullWidth={isMobile}>
                        Start Draft
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Complete Checklist Dialog */}
            <Dialog
                open={completeDialogOpen}
                onClose={() => setCompleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>{selectedTemplate?.name || 'Complete Checklist'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Link this checklist to an operation or drone (optional)
                        </Alert>
                        <TextField
                            fullWidth
                            label="Operation ID (Optional)"
                            type="number"
                            value={completeForm.operationId || ''}
                            onChange={(e) => setCompleteForm({ ...completeForm, operationId: e.target.value })}
                            margin="normal"
                            size="small"
                        />
                        <TextField
                            fullWidth
                            label="Drone ID (Optional)"
                            type="number"
                            value={completeForm.droneId || ''}
                            onChange={(e) => setCompleteForm({ ...completeForm, droneId: e.target.value })}
                            margin="normal"
                            size="small"
                            sx={{ mb: 3 }}
                        />
                        {completeForm.items.map((item) => (
                            <FormControlLabel
                                key={item.id}
                                control={
                                    <Checkbox
                                        checked={item.checked}
                                        onChange={() => handleToggleItem(item.id)}
                                    />
                                }
                                label={
                                    <Box>
                                        {item.text}
                                        {item.required && <Typography component="span" color="error"> *</Typography>}
                                    </Box>
                                }
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    mb: 1,
                                    '& .MuiFormControlLabel-label': {
                                        pt: 0.5
                                    }
                                }}
                            />
                        ))}
                        <TextField
                            fullWidth
                            label="Notes (Optional)"
                            value={completeForm.notes}
                            onChange={(e) => setCompleteForm({ ...completeForm, notes: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0, p: isMobile ? 2 : 1 }}>
                    <Button onClick={() => setCompleteDialogOpen(false)} fullWidth={isMobile}>
                        Cancel
                    </Button>
                    <Button onClick={handleCompleteChecklist} variant="contained" fullWidth={isMobile}>
                        Complete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Template Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
                <DialogTitle>Delete Template</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this template? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0, p: isMobile ? 2 : 1 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} fullWidth={isMobile}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteTemplate} color="error" variant="contained" fullWidth={isMobile}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Clone Template Dialog */}
            <Dialog
                open={cloneDialogOpen}
                onClose={() => setCloneDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>Clone Template</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Template Name"
                            value={cloneForm.name}
                            onChange={(e) => setCloneForm({ ...cloneForm, name: e.target.value })}
                            margin="normal"
                            required
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={cloneForm.description}
                            onChange={(e) => setCloneForm({ ...cloneForm, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <Alert severity="info" sx={{ mt: 2 }}>
                            This will create a copy of the template that you can customize for your organization.
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0, p: isMobile ? 2 : 1 }}>
                    <Button onClick={() => setCloneDialogOpen(false)} fullWidth={isMobile}>
                        Cancel
                    </Button>
                    <Button onClick={handleCloneTemplate} variant="contained" startIcon={<CloneIcon />} fullWidth={isMobile}>
                        Clone Template
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Draft Checklist Dialog */}
            <Dialog
                open={draftDialogOpen}
                onClose={() => setDraftDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                {isMobile && (
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={() => setDraftDialogOpen(false)}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                            <Box sx={{ ml: 2, flex: 1 }}>
                                <Typography variant="h6" component="div">
                                    {selectedDraft?.template?.name || 'Edit Draft'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                    Draft Mode
                                </Typography>
                            </Box>
                            <Button autoFocus color="inherit" onClick={handleSubmitDraft} startIcon={<CompleteIcon />}>
                                Submit
                            </Button>
                        </Toolbar>
                    </AppBar>
                )}
                {!isMobile && (
                    <DialogTitle>
                        {selectedDraft?.template?.name || 'Edit Draft Checklist'}
                        <Typography variant="caption" display="block" color="text.secondary">
                            Draft Mode - Save progress or submit when complete
                        </Typography>
                    </DialogTitle>
                )}
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {completeForm.items.map((item) => (
                            <Box key={item.id} mb={2}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={item.checked}
                                            onChange={() => handleToggleItem(item.id)}
                                        />
                                    }
                                    label={
                                        <Box>
                                            {item.text}
                                            {item.required && <Typography component="span" color="error"> *</Typography>}
                                        </Box>
                                    }
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        '& .MuiFormControlLabel-label': {
                                            pt: 0.5
                                        }
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Add notes for this item..."
                                    value={item.notes || ''}
                                    onChange={(e) => {
                                        const newItems = completeForm.items.map(i =>
                                            i.id === item.id ? { ...i, notes: e.target.value } : i
                                        );
                                        setCompleteForm({ ...completeForm, items: newItems });
                                    }}
                                    sx={{ ml: 4, mt: 0.5 }}
                                />
                            </Box>
                        ))}
                        <TextField
                            fullWidth
                            label="Overall Notes (Optional)"
                            value={completeForm.notes}
                            onChange={(e) => setCompleteForm({ ...completeForm, notes: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                    </Box>
                </DialogContent>
                {isMobile ? (
                    <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: 'background.paper' }}>
                        <Button
                            onClick={handleUpdateDraft}
                            startIcon={<SaveIcon />}
                            fullWidth
                            variant="outlined"
                        >
                            Save Draft
                        </Button>
                    </Box>
                ) : (
                    <DialogActions>
                        <Button onClick={() => setDraftDialogOpen(false)}>
                            Close
                        </Button>
                        <Button onClick={handleUpdateDraft} startIcon={<SaveIcon />}>
                            Save Draft
                        </Button>
                        <Button onClick={handleSubmitDraft} variant="contained" startIcon={<CompleteIcon />}>
                            Submit
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={hideToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={hideToast} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Checklists;
